/**
 * Form Submission Handler with Bot Protection
 * 
 * This utility handles form submissions with reCAPTCHA v3 token generation
 * and API request handling with proper error management.
 */

import { BOT_PROTECTION, API_ENDPOINTS } from '@/config/endpoints'

declare global {
  interface Window {
    grecaptcha?: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>
      ready: (callback: () => void) => void
    }
  }
}

/**
 * Load reCAPTCHA script if bot protection is enabled
 */
export const loadRecaptcha = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!BOT_PROTECTION.enabled || !BOT_PROTECTION.siteKey) {
      resolve()
      return
    }

    if (window.grecaptcha) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js'
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => resolve())
      } else {
        reject(new Error('reCAPTCHA failed to load'))
      }
    }
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'))
    document.head.appendChild(script)
  })
}

/**
 * Get reCAPTCHA token for form submission
 * @param action - The action name for reCAPTCHA (e.g., 'submit_demo_form')
 */
export const getRecaptchaToken = async (action: string): Promise<string | null> => {
  if (!BOT_PROTECTION.enabled || !BOT_PROTECTION.siteKey || !window.grecaptcha) {
    return null
  }

  try {
    return await window.grecaptcha.execute(BOT_PROTECTION.siteKey, { action })
  } catch (error) {
    console.error('Failed to get reCAPTCHA token:', error)
    return null
  }
}

/**
 * Submit demo form with bot protection
 */
export const submitDemoForm = async (formData: {
  name: string
  email: string
  company: string
  marketplaces: string
  message: string
}): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const endpoint = API_ENDPOINTS.demoForm

    // Check if endpoint is configured (not the placeholder)
    if (!endpoint || endpoint.includes('your-backend')) {
      // If no backend is configured, simulate success but log the data
      console.log('Demo form submission (no backend configured):', formData)
      return {
        success: true,
        message: 'Thank you for your interest! We have received your demo request. Our team will contact you within 24 hours.'
      }
    }

    // Get reCAPTCHA token
    const token = await getRecaptchaToken('submit_demo_form')

    // Prepare request body
    const body = {
      ...formData,
      token
    }

    // Send POST request
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    const result = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `Server error: ${response.status}`
      }
    }

    return {
      success: true,
      message: result.message || 'Demo request submitted successfully'
    }
  } catch (error) {
    console.error('Demo form submission error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An error occurred while submitting the form'
    }
  }
}

/**
 * Submit contact form with bot protection
 */
export const submitContactForm = async (formData: {
  name: string
  email: string
  company: string
  message: string
}): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const endpoint = API_ENDPOINTS.contactForm

    // Check if endpoint is configured (not the placeholder)
    if (!endpoint || endpoint.includes('your-backend')) {
      // If no backend is configured, simulate success but log the data
      console.log('Contact form submission (no backend configured):', formData)
      return {
        success: true,
        message: 'Thank you for your message! We have received your inquiry. Our team will get back to you as soon as possible.'
      }
    }

    // Get reCAPTCHA token
    const token = await getRecaptchaToken('submit_contact_form')

    // Prepare request body
    const body = {
      ...formData,
      token
    }

    // Send POST request
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    const result = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `Server error: ${response.status}`
      }
    }

    return {
      success: true,
      message: result.message || 'Your message has been sent successfully'
    }
  } catch (error) {
    console.error('Contact form submission error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An error occurred while submitting the form'
    }
  }
}
